import {
  getComponentSchemas,
  getRefName,
  hasAnySelected,
  isDiscriminator,
  isObject,
  toModules,
  type JsonObject,
} from './openapi-utils';
import type { ModuleId } from './module-labels';

const moduleCollator = new Intl.Collator(undefined, { numeric: true });

/** Sorts module names numerically by segment, so "4.1" < "15.2" < "15.10", with named modules last. */
export const compareModules = (a: string, b: string) => moduleCollator.compare(a, b);

export type ModuleIndex = {
  /** Every distinct x-modules value in the document, sorted */
  modules: ModuleId[];
  /** Modules declared on each node or anywhere beneath it, following $refs. Nodes with none are absent. */
  subtreeModules: WeakMap<object, ReadonlySet<ModuleId>>;
};

/**
 * Walks the whole document once, recording which modules are reachable from every node. $refs to component
 * schemas are resolved so that a property pointing at a shared schema knows about the modules inside it.
 */
export function buildModuleIndex(spec: JsonObject): ModuleIndex {
  const subtreeModules = new WeakMap<object, ReadonlySet<ModuleId>>();
  const visited = new WeakSet<object>();
  const allModules = new Set<ModuleId>();
  const components = getComponentSchemas(spec);

  const visit = (node: unknown): ReadonlySet<ModuleId> | undefined => {
    if (typeof node !== 'object' || node === null) return undefined;
    // Also guards against cycles through $refs; a node mid-visit contributes nothing to its own subtree
    if (visited.has(node)) return subtreeModules.get(node);
    visited.add(node);

    const found = new Set<ModuleId>();
    const merge = (modules: ReadonlySet<ModuleId> | undefined) => modules?.forEach((m) => found.add(m));

    if (Array.isArray(node)) {
      node.forEach((child) => merge(visit(child)));
    } else {
      const obj = node as JsonObject;
      toModules(obj['x-modules']).forEach((m) => {
        found.add(m);
        allModules.add(m);
      });
      const refName = getRefName(obj.$ref);
      if (refName !== undefined) merge(visit(components?.[refName]));
      Object.values(obj).forEach((child) => merge(visit(child)));
    }

    if (found.size > 0) subtreeModules.set(node, found);
    return found.size > 0 ? found : undefined;
  };

  visit(spec);

  return { modules: [...allModules].sort(compareModules), subtreeModules };
}

/**
 * Returns a copy of the document containing only the parts of each schema relevant to the selected modules.
 * A property is kept if it, or anything beneath it, is tagged with a selected module. Everything under a
 * matching node is kept intact. Schemas with no x-modules anywhere (e.g. responses) are left untouched.
 * Unchanged subtrees are shared with the original document rather than copied.
 */
export function filterSpecByModules(spec: JsonObject, index: ModuleIndex, selected: ReadonlySet<ModuleId>): JsonObject {
  if (selected.size === 0) return spec;

  const memo = new WeakMap<object, unknown>();

  const matches = (node: unknown) => isObject(node) && hasAnySelected(index.subtreeModules.get(node), selected);

  const ownMatch = (node: JsonObject) =>
    Array.isArray(node['x-modules']) && hasAnySelected(node['x-modules'], selected);

  const filterNode = (node: unknown): unknown => {
    if (typeof node !== 'object' || node === null) return node;
    // Nothing tagged beneath here, so there is nothing to filter
    if (!index.subtreeModules.has(node)) return node;
    if (memo.has(node)) return memo.get(node);

    let result: unknown;
    if (Array.isArray(node)) {
      result = node.map(filterNode);
    } else if (ownMatch(node as JsonObject)) {
      result = node;
    } else {
      const copy: JsonObject = {};
      for (const [key, value] of Object.entries(node as JsonObject)) {
        if (key === 'properties' && isObject(value)) {
          copy[key] = Object.fromEntries(
            Object.entries(value)
              .filter(([, child]) => matches(child) || isDiscriminator(child))
              .map(([name, child]) => [name, filterNode(child)]),
          );
        } else if ((key === 'oneOf' || key === 'anyOf') && Array.isArray(value)) {
          const kept = value.filter(matches);
          // Keep the original variants rather than leaving an empty (invalid) composition
          copy[key] = (kept.length > 0 ? kept : value).map(filterNode);
        } else {
          copy[key] = filterNode(value);
        }
      }
      result = copy;
    }

    memo.set(node, result);
    return result;
  };

  return filterNode(spec) as JsonObject;
}
